import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import copy from 'copy-to-clipboard';
import Axios from 'axios';

import {
  Box, Button, Checkbox, CircularProgress, Dialog, FormControl, Grid, IconButton, MenuItem, OutlinedInput, Select, SelectChangeEvent, Tooltip, Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  FileCopyOutlined as CopyIcon,
  CheckCircleOutline as CheckIcon,
  Save as SaveIcon,
  Visibility as ShowPasswordIcon,
  VisibilityOff as HidePasswordIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import ConfigContext from 'src/contexts/ConfigContext';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import { appendContactSupport, axiosHeaders, getLocalisedErrorString, validateEmail } from 'src/utils/helpers';
import { integrationNames, userRoles } from 'src/config';
import { CustomError } from 'src/types';
import styles from './style';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  nodeType: string | null;
  nodeId: string | null;
  label: string | null;
  configuration: Record<string, any>;
  isSaving?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfigurationSave: (nodeId: string, configuration: Record<string, any>) => void;
}

const DocumentExportComponent = (props: PropTypes) => {
  const { ready, t } = useTranslation();
  const { user } = useAuth();
  const { API } = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();

  const { open, onClose, nodeType, nodeId, configuration, isSaving = false, onConfigurationSave } = props;

  const [loading, setLoading] = useState(false);

  const [exportFormats] = useState(['csv', 'dat', 'xlsx', 'xml', 'json']);
  const [exportDestinations] = useState([{
    value: 'app', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_APP'
  }, {
    value: 'email', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_EMAIL'
  }, {
    value: 'tf', label: 'WORKFLOW_COMPONENT_EXPORT_DESTINATION_TF'
  }, {
    value: 'qb', label: 'WORKFLOW_COMPONENT_EXPORT_DESTINATION_QB'
  }, {
    value: 'holded', label: 'WORKFLOW_COMPONENT_EXPORT_DESTINATION_HOLDED'
  }, {
    value: 'gstock', label: 'WORKFLOW_COMPONENT_EXPORT_DESTINATION_GSTOCK'
  }, {
    value: 'sftp', label: 'WORKFLOW_COMPONENT_EXPORT_DESTINATION_SFTP'
  }, {
    value: 'api', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_API'
  }]);
  const [selectedExportFormat, setSelectedExportFormat] = useState<string>('');
  const [selectedExportDestinations, setSelectedExportDestinations] = useState<string[]>([]);

  const [usedEmails, setUsedEmails] = useState(user?.deliverEmails?.join(', ') || '');
  const [tfCompanyCode, setTFCompanyCode] = useState('');
  const [holdedApikey, setHoldedApikey] = useState('');
  const [gstockClientID, setGstockClientID] = useState('');
  const [gstockClientSecret, setGstockClientSecret] = useState('');
  const [sftpData, setSftpData] = useState<Record<string, string>>({});

  const [apiConfig, setApiConfig] = useState({
    method: 'POST',
    url: '',
    authorizationType: 'basic-auth',
    username: '',
    password: '',
    bearerToken: '',
    key: '',
    value: ''
  });
  const [showApiPassword, setShowApiPassword] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [showApiValue, setShowApiValue] = useState(false);
  const [isApiFieldChanged, setIsApiFieldChanged] = useState(false);

  const [appError, setAppError] = useState({
    tf: false,
    qb: false,
    holded: false,
    gstock: false,
    sftp: false,
    api: false,
  });
  const [saved, setSaved] = useState({
    email: false,
    tfCompanyCode: false,
    holded: false,
    gstockClientID: false,
    gstockClientSecret: false,
    hostSFTP: false,
    portSFTP: false,
    usernameSFTP: false,
    passwordSFTP: false,
    folderPathSFTP: false,
  });
  const [iUsedEmails, setIUsedEmails] = useState(user?.deliverEmails || []);
  const [tfAuthURL, setTFAuthURL] = useState('');
  const [qbOauth2URL, setQBOauth2URL] = useState('');
  const [isFieldChangedSFTP, setIsFieldChangedSFTP] = useState<Record<string, boolean>>({});
  const [showSftpPassword, setShowSftpPassword] = useState(false);

  const [copyObj, setCopyObj] = useState<Record<string, boolean>>({
    tfAuthURL: false,
  });

  const onChangeCopy = (prop: string) => {
    const iCopyObj: Record<string, boolean> = {
      tfAuthURL: false,
    };

    iCopyObj[prop] = true;

    setCopyObj(iCopyObj);
  };

  const copyToClipboard = (prop: string, value: string) => {
    if (value === '') return;
    copy(value, {
      // @ts-ignore
      onCopy: onChangeCopy(prop)
    });
  };

  const handleClearSelections = () => {
    setSelectedExportFormat('');
    setSelectedExportDestinations([]);
    setCopyObj({
      tfAuthURL: false,
    });
  };

  const handleChangeExportFormat = (event: SelectChangeEvent<typeof selectedExportFormat>) => {
    setSelectedExportFormat(event.target.value);
  };

  const handleChangeExportDestination = (event: SelectChangeEvent<typeof selectedExportDestinations>) => {
    const value = event.target.value;
    const newDestinations = typeof value === 'string' ? value.split(',') : value;

    const fileDestinations = ['app', 'email', 'sftp'];
    const apiDestinations = ['tf', 'qb', 'holded', 'gstock', 'api'];

    const hasFileDestination = newDestinations.some(dest => fileDestinations.includes(dest));
    const hasApiDestination = newDestinations.some(dest => apiDestinations.includes(dest));

    let filteredDestinations = newDestinations;

    if (hasFileDestination && hasApiDestination) {
      const lastSelected = newDestinations[newDestinations.length - 1];

      if (fileDestinations.includes(lastSelected)) {
        filteredDestinations = newDestinations.filter(dest => fileDestinations.includes(dest));
      } else {
        filteredDestinations = newDestinations.filter(dest => apiDestinations.includes(dest));
      }
    }

    setSelectedExportDestinations(filteredDestinations);
    setCopyObj({
      tfAuthURL: false,
    });
  };

  const getSavedDetailsTF = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsTf}/${user?.companyID}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setTFCompanyCode(response.data.data.officeCode);
        setAppError((prev) => {
          return {
            ...prev,
            tf: false,
          }
        });
      }
    } catch (error) {
      setAppError((prev) => {
        return {
          ...prev,
          tf: true,
        }
      });
    }
  };

  const getSavedDetailsQB = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsQuickbooks}/${user?.companyID}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setQBOauth2URL(response.data.data.oauth2URL);
        setAppError((prev) => {
          return {
            ...prev,
            qb: false,
          }
        });
      }
    } catch (error) {
      setAppError((prev) => {
        return {
          ...prev,
          qb: true,
        }
      });
    }
  };

  const getSavedDetailsHolded = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsHolded}/${user?.companyID}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setHoldedApikey(response.data.data.apikey);
        setAppError((prev) => {
          return {
            ...prev,
            holded: false,
          }
        });
      }
    } catch (error) {
      setAppError((prev) => {
        return {
          ...prev,
          holded: true,
        }
      });
    }
  };

  const getSavedDetailsGstock = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsGStock}/${user?.companyID}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setGstockClientID(response.data.data.clientId);
        setGstockClientSecret(response.data.data.clientSecret);
        setAppError((prev) => {
          return {
            ...prev,
            gstock: false,
          }
        });
      }
    } catch (error) {
      setAppError((prev) => {
        return {
          ...prev,
          gstock: true,
        }
      });
    }
  };

  const getSavedDetailsSftp = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsSFTP}/${integrationNames.sftpSender}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setSftpData(response.data.data);
        setAppError((prev) => {
          return {
            ...prev,
            sftp: false,
          }
        });
      }
    } catch (error) {
      setAppError((prev) => {
        return {
          ...prev,
          sftp: true,
        }
      });
    }
  };

  // API configuration handlers
  const isValidUrl = (url: string) => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isApiFormValid = () => {
    if (!apiConfig || !apiConfig.url || !isValidUrl(apiConfig.url)) return false;
    switch (apiConfig.authorizationType) {
      case 'basic-auth':
        return Boolean(apiConfig.username && apiConfig.password);
      case 'bearer-token':
        return Boolean(apiConfig.bearerToken);
      case 'api-key':
        return Boolean(apiConfig.key && apiConfig.value);
      default:
        return false;
    }
  };

  const getSavedDetailsApi = async () => {
    try {
      const response = await Axios.get(
        `${API.integrations}/export/api`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        let responseData = response.data.data;
        if (!responseData || typeof responseData !== 'object' || Object.keys(responseData).length === 0) {
          responseData = {
            method: 'POST',
            url: '',
            authorizationType: 'basic-auth',
            username: '',
            password: '',
            bearerToken: '',
            key: '',
            value: ''
          };
        } else {
          responseData = {
            method: responseData.method || 'POST',
            url: responseData.url || '',
            authorizationType: responseData.authorizationType || 'basic-auth',
            username: responseData.username || '',
            password: responseData.password || '',
            bearerToken: responseData.bearerToken || '',
            key: responseData.key || '',
            value: responseData.value || ''
          };
        }
        setApiConfig(responseData);
        setAppError((prev) => ({
          ...prev,
          api: false,
        }));
      }
    } catch (error) {
      setAppError((prev) => ({
        ...prev,
        api: true,
      }));
    }
  };

  const onCheckApiConnection = async () => {
    if (!isApiFormValid()) {
      enqueueSnackbar(
        t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS'),
        {
          variant: 'warning',
          autoHideDuration: 5000
        }
      );
      return;
    }

    setLoading(true);
    try {
      let headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      switch (apiConfig.authorizationType) {
        case 'basic-auth':
          headers['Authorization'] = `Basic ${btoa(`${apiConfig.username}:${apiConfig.password}`)}`;
          break;
        case 'bearer-token':
          headers['Authorization'] = `Bearer ${apiConfig.bearerToken}`;
          break;
        case 'api-key':
          headers[apiConfig.key] = apiConfig.value;
          break;
      }

      const exampleBody = {
        id: 123456,
        email: user?.email || "user@example.com",
        company: user?.company || "Test Company",
        creationTime: Date.now(),
        documentFile: "test-invoice.pdf",
        fileName: "test-invoice.pdf",
        processedDocType: "INVOICE",
        status: 502
      };

      const response = await Axios({
        method: apiConfig.method || 'POST',
        url: apiConfig.url,
        headers: headers,
        data: JSON.stringify([exampleBody], null, 2),
      });

      if (response.status >= 200 && response.status < 300) {
        enqueueSnackbar(
          t('CUSTOMER_INTEGRATIONS_ENDPOINT_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
      } else {
        throw new Error(`Unexpected response: ${response.status}`);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      const customError = error as any;
      let errMsg;

      if (customError.message && customError.message.toLowerCase() === 'network error') {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED_NETWORK_OR_CORS');
      } else if (customError.response) {
        errMsg = t('INTEGRATIONS_API_CONNECTION_FAILED_WITH_STATUS', { status: customError.response.status });
      } else if (customError.request) {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED_NO_RESPONSE');
      } else {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED');
      }

      enqueueSnackbar(
        errMsg,
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const loadData = async () => {
    setLoading(true);
    await getSavedDetailsTF();
    await getSavedDetailsQB();
    await getSavedDetailsHolded();
    await getSavedDetailsGstock();
    await getSavedDetailsSftp();
    await getSavedDetailsApi();
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load configuration when component opens
  useEffect(() => {
    if (nodeType === 'document_export' && configuration) {
      setSelectedExportFormat(configuration.selectedExportFormat || '');
      setSelectedExportDestinations(configuration.selectedExportDestinations
        || configuration.selectedExportDestination ? [configuration.selectedExportDestination] : []);
      if (configuration.configs) {
        if (configuration.configs.emails) {
          setUsedEmails(configuration.configs.emails);
        }
        if (configuration.configs.officeCode) {
          setTFCompanyCode(configuration.configs.officeCode);
        }
        if (configuration.configs.apiKey) {
          setHoldedApikey(configuration.configs.apiKey);
        }
        if (configuration.configs.clientId) {
          setGstockClientID(configuration.configs.clientId);
        }
        if (configuration.configs.clientSecret) {
          setGstockClientSecret(configuration.configs.clientSecret);
        }
        if (configuration.configs.sftpData) {
          setSftpData(configuration.configs.sftpData);
        }
        if (configuration.configs.apiConfig) {
          setApiConfig(configuration.configs.apiConfig);
        }
      }
    }
  }, [nodeType, configuration]);

  const saveNewDetailsEmail = async (_usedEmails: string) => {
    if (!_usedEmails) {
      return;
    }
    const uEmails = _usedEmails.split(',');

    const fullList = [];
    const addedEmails = [];
    const removedEmails = [];
    for (let i = 0; i < uEmails.length; i++) {
      if (uEmails[i] && uEmails[i].trim()) {
        fullList.push(uEmails[i].trim());
      }

      let isNew = true;
      for (let j = 0; j < iUsedEmails.length; j++) {
        if (uEmails[i] && iUsedEmails[j] && uEmails[i].trim() === iUsedEmails[j].trim()) {
          isNew = false;
          break;
        }
      }

      if (isNew && uEmails[i] && uEmails[i].trim()) {
        const resp = validateEmail(uEmails[i].trim());
        if (!resp.isValid && resp.errorMessage) {
          enqueueSnackbar(
            t(resp.errorMessage),
            {
              variant: 'error',
              autoHideDuration: 5000
            }
          );
          return;
        }
        addedEmails.push(uEmails[i].trim());
      }
    }

    for (let i = 0; i < iUsedEmails.length; i++) {
      let isDeleted = true;
      for (let j = 0; j < uEmails.length; j++) {
        if (iUsedEmails[i] && uEmails[j] && iUsedEmails[i].trim() === uEmails[j].trim()) {
          isDeleted = false;
          break;
        }
      }

      if (isDeleted) {
        removedEmails.push(iUsedEmails[i].trim());
      }
    }

    if (addedEmails.length <= 0 && removedEmails.length <= 0) {
      return;
    }

    try {
      const response = await Axios.post(
        `${API.saveEmailDeliver}`,
        {
          new: addedEmails,
          old: removedEmails
        },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setSaved((prev) => {
          return {
            ...prev,
            email: true
          }
        });
        setIUsedEmails(fullList);
      } else {
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS'), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const saveNewDetailsTF = async (companyCode: string) => {
    if (!companyCode) {
      return;
    }
    try {
      const response = await Axios.post(
        `${API.savedDetailsTf}/${user?.companyID}`,
        {
          officeCode: companyCode
        },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setSaved((prev) => {
          return {
            ...prev,
            tfCompanyCode: true
          }
        });
      } else {
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onGenerateAuthURL = () => {
    setTFAuthURL(
      `https://login.twinfield.com/auth/authentication/connect/authorize?client_id=PROCYS_APP
      &redirect_uri=https://${window.location.host}/integrations/twinfield/token/callback
      &response_type=code&scope=openid+twf.user+twf.organisation+twf.organisationUser+offline_access
      &nonce=qqq&state=${user?.email}__BR__${user?.companyID}`.replace(/ /g, '')
    );
  };

  const onCheckConnectionTF = async () => {
    try {
      const response = await Axios.get(
        `${API.savedDetailsTf}`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('INTEGRATIONS_TF_CHECK_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
      } else {
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onQBOAuth2Connect = () => {
    window.open(qbOauth2URL, '_blank');
  };

  const onCheckConnectionQB = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.checkQuickbooksConnection}/${user?.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('INTEGRATIONS_GSTOCK_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        setLoading(false);
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onSyncQB = async () => {
    setLoading(true);
    try {
      await Axios.get(
        API.syncQuickbooks,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      setTimeout(() => {
        enqueueSnackbar(
          t('INTEGRATIONS_SYNC_STARTED_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      }, 500);
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const saveNewDetailsHolded = async (key: string) => {
    setLoading(true);
    try {
      const response = await Axios.post(
        `${API.savedDetailsHolded}/${user?.companyID}`,
        {
          apikey: key,
        },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setLoading(false);
        setSaved((prev) => {
          return {
            ...prev,
            holded: true
          }
        });
        getSavedDetailsHolded();
      } else {
        setLoading(false);
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onCheckConnectionHolded = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.checkHoldedConnection}/${user?.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('INTEGRATIONS_GSTOCK_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        setLoading(false);
        enqueueSnackbar(
          t('INTEGRATIONS_HOLDED_ERR_CHECK_CONN'),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onSyncHolded = async () => {
    setLoading(true);
    try {
      await Axios.post(
        `${API.syncHolded}/${user?.companyID}`,
        {},
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      setTimeout(() => {
        enqueueSnackbar(
          t('INTEGRATIONS_SYNC_STARTED_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      }, 500);
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const saveNewDetailsGstock = async (id: string, secret: string, num: number) => {
    setLoading(true);
    try {
      const response = await Axios.post(
        `${API.savedDetailsGStock}/${user?.companyID}`,
        {
          clientID: id,
          clientSecret: secret
        },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setSaved((prev) => {
          if (num === 0) {
            return {
              ...prev,
              gstockClientID: true
            }
          } else {
            return {
              ...prev,
              gstockClientSecret: true
            }
          }
        });
        setLoading(false);
      } else {
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onCheckConnectionGstock = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.checkGStockConnection}/${user?.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('INTEGRATIONS_GSTOCK_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onSyncGstock = async () => {
    setLoading(true);
    try {
      await Axios.get(
        API.syncGstock,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      setLoading(false);
      setTimeout(() => {
        enqueueSnackbar(
          t('INTEGRATIONS_SYNC_STARTED_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
      }, 500);
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onChangeSFTPDetails = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;

    setSftpData({
      ...sftpData,
      [name]: value
    });

    setIsFieldChangedSFTP((prev) => {
      return {
        ...prev,
        [name]: true
      }
    });
  };

  const onCallSaveSFTPDetails = async (data: Record<string, string>) => {
    try {
      const response = await Axios.post(
        `${API.savedDetailsSFTP}/${integrationNames.sftpSender}`, data, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setSftpData(response.data.data);
        Object.keys(isFieldChangedSFTP).forEach((key) => {
          setSaved((prev) => {
            return {
              ...prev,
              [`${key}SFTP`]: true
            }
          });
        });
        getSavedDetailsSftp();
        setLoading(false);
      } else {
        setIsFieldChangedSFTP({});
        setAppError((prev) => {
          return {
            ...prev,
            sftp: true,
          }
        });
        setLoading(false);
      }
    } catch (error) {
      setIsFieldChangedSFTP({});
      setLoading(false);
      setAppError((prev) => {
        return {
          ...prev,
          sftp: true,
        }
      });
    }
  };

  const saveSFTPDetails = async (event: React.FocusEvent<HTMLInputElement, Element>) => {
    setLoading(true);
    event.preventDefault();
    const { name, value } = event.target;

    const data: Record<string, string> = {
      host: sftpData?.host,
      port: sftpData?.port,
      username: sftpData?.username,
      password: sftpData?.password,
      folderPath: sftpData?.folderPath,
      exportFormat: selectedExportFormat,
    };

    data[name] = value;

    onCallSaveSFTPDetails(data);
  };

  const onCheckConnectionSFTP = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.testSFTPConnection}/${integrationNames.sftpSender}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('INTEGRATIONS_GSTOCK_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        setLoading(false);
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (e) {
      const error = e as CustomError;
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const handleApiConfigChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setApiConfig({
      ...apiConfig,
      [name]: value
    });
    setIsApiFieldChanged(true);
  };

  const saveApiDetails = async () => {
    setLoading(true);
    try {
      const response = await Axios.post(
        `${API.integrations}/export/api`,
        apiConfig,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        setApiConfig(response.data.data || apiConfig);
        setAppError((prev) => ({ ...prev, api: false }));
        setIsApiFieldChanged(false);
        enqueueSnackbar(
          t('INTEGRATIONS_API_DETAILS_SAVED_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        setLoading(false);
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('INTEGRATIONS_ERROR_SAVE_DETAILS'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      let errMsg = t('INTEGRATIONS_ERROR_SAVE_DETAILS');
      if (error && typeof error === 'object' && 'response' in error) {
        const customError = error as CustomError;
        if (customError?.response?.data) {
          errMsg = getLocalisedErrorString(customError.response.data.i18n, t);
        }
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const getAvailableDestinations = () => {
    const fileDestinations = ['app', 'email', 'sftp'];
    const apiDestinations = ['tf', 'qb', 'holded', 'gstock', 'api'];

    if (selectedExportFormat === '') {
      if (selectedExportDestinations.length === 0) {
        return exportDestinations;
      }

      const hasFileDestination = selectedExportDestinations.some(dest => fileDestinations.includes(dest));
      const hasApiDestination = selectedExportDestinations.some(dest => apiDestinations.includes(dest));

      if (hasFileDestination) {
        return exportDestinations.filter(dest => fileDestinations.includes(dest.value));
      } else if (hasApiDestination) {
        return exportDestinations.filter(dest => apiDestinations.includes(dest.value));
      }

      return exportDestinations;
    }

    const fileFormats = ['csv', 'dat', 'xlsx'];
    const apiFormats = ['json'];

    if (fileFormats.includes(selectedExportFormat)) {
      return exportDestinations.filter(dest => fileDestinations.includes(dest.value));
    } else if (apiFormats.includes(selectedExportFormat)) {
      return exportDestinations.filter(dest => apiDestinations.includes(dest.value));
    }

    return exportDestinations;
  };

  const getAvailableFormats = () => {
    if (selectedExportDestinations.length === 0) {
      return exportFormats;
    }

    const fileDestinations = ['app', 'email', 'sftp'];
    const apiDestinations = ['tf', 'qb', 'holded', 'gstock', 'api'];

    const hasFileDestination = selectedExportDestinations.some(dest => fileDestinations.includes(dest));
    const hasApiDestination = selectedExportDestinations.some(dest => apiDestinations.includes(dest));

    if (hasFileDestination && !hasApiDestination) {
      return ['csv', 'dat', 'xlsx'];
    } else if (hasApiDestination && !hasFileDestination) {
      return ['json'];
    }

    return exportFormats;
  };

  const isDestinationSelected = (destination: string) => {
    return selectedExportDestinations.includes(destination);
  };

  const handleDocumentExportSave = () => {
    if (nodeId) {
      let configs: Record<string, any> = {};

      selectedExportDestinations.forEach(destination => {
        if (destination === 'sftp') {
          configs.sftpData = sftpData;
        } else if (destination === 'gstock') {
          configs.clientId = gstockClientID;
          configs.clientSecret = gstockClientSecret;
        } else if (destination === 'email') {
          configs.emails = usedEmails;
        } else if (destination === 'tf') {
          configs.officeCode = tfCompanyCode;
        } else if (destination === 'holded') {
          configs.apiKey = holdedApikey;
        } else if (destination === 'api') {
          configs.apiConfig = apiConfig;
        }
      });

      const configuration = {
        selectedExportFormat: selectedExportFormat,
        selectedExportDestinations: selectedExportDestinations,

        selectedExportDestination: selectedExportDestinations[0] || '',
        configs: configs
      };

      onConfigurationSave(nodeId, configuration);
    }
  };

  return (
    <Dialog
      open={open}
      sx={styles.modal}
      onClose={onClose}
    >
      <Box sx={styles.container}>
        <Box sx={styles.modalHeader}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.headerTitle}>
              {t('WORKFLOW_COMPONENT_EXPORT')}
            </Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>
        <Grid sx={styles.modalContent} className="y-scroll">
          <Box sx={styles.addNewForm}>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.descr}>{t('WORKFLOW_COMPONENT_EXPORT_DESCRIPTION')}</Typography>
            </Box>
            <Box sx={styles.fieldContainer}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                {(selectedExportFormat || selectedExportDestinations.length > 0) && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearSelections}
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    {ready && t('WORKFLOWS_COMMON_CLEAR')}
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>{t('WORKFLOW_COMPONENT_EXPORT_FORMAT')}</Typography>
              <FormControl fullWidth sx={styles.formControl}>
                <Select
                  id="export-format"
                  value={selectedExportFormat}
                  onChange={handleChangeExportFormat}
                  input={<OutlinedInput />}
                  sx={styles.selectStyle}
                >
                  {getAvailableFormats().map((format) => (
                    <MenuItem sx={styles.selectItem} key={format} value={format}>
                      {format}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>{t('WORKFLOW_COMPONENT_EXPORT_DESTINATION')}</Typography>
              <FormControl fullWidth sx={styles.formControl}>
                <Select
                  id="export-destination"
                  multiple
                  value={selectedExportDestinations}
                  onChange={handleChangeExportDestination}
                  input={<OutlinedInput />}
                  sx={styles.selectStyle}
                  renderValue={(selected) => (selected as string[]).map(value => {
                    const dest = exportDestinations.find(d => d.value === value);
                    return dest ? t(dest.label) : value;
                  }).join(', ')}
                >
                  {getAvailableDestinations().map((destination) => (
                    <MenuItem sx={styles.selectItem} key={destination.value} value={destination.value}>
                      <Checkbox sx={styles.checkBox} checked={selectedExportDestinations.includes(destination.value)} />
                      {t(destination.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {isDestinationSelected('email') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_EMAIL')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_EMAIL_FORWARDING_EMAIL')}</Typography>
                    {saved.email && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={usedEmails}
                      style={styles.inputField}
                      onChange={(e) => setUsedEmails(e.target.value)}
                      onBlur={(e) => saveNewDetailsEmail(e.target.value)}
                    />
                  </Box>
                </Box>
              </>
            )}
            {isDestinationSelected('tf') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_EXPORT_DESTINATION_TF')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.tf && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_TF_COMPANY_CODE')}</Typography>
                    {saved.tfCompanyCode && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={tfCompanyCode}
                      style={styles.inputField}
                      onChange={(e) => setTFCompanyCode(e.target.value)}
                      onBlur={(e) => saveNewDetailsTF(e.target.value)}
                    />
                  </Box>
                </Box>
                <Typography onClick={onGenerateAuthURL} sx={styles.link}>{ready && t('INTEGRATIONS_TF_GENERATE_AUTH_URL')}</Typography>
                {tfAuthURL && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('INTEGRATIONS_TF_AUTH_URL')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={tfAuthURL}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('tfAuthURL', tfAuthURL)}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.tfAuthURL && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onCheckConnectionTF}
                  >
                    {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                  </Button>
                </Box>
              </>
            )}
            {isDestinationSelected('qb') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_EXPORT_DESTINATION_QB')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.qb && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.label}>{t('QUICKBOOKS_CLICK_HERE')}</Typography>
                  <Box sx={styles.detailRow}>
                    <Button
                      variant="outlined"
                      sx={styles.checkConnectionBtn}
                      onClick={onQBOAuth2Connect}
                      disabled={loading}
                    >
                      {ready && t('QUICKBOOKS_CONNECT')}
                    </Button>
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer} />
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onCheckConnectionQB}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                  </Button>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onSyncQB}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_QUICKBOOKS_SYNC')}
                  </Button>
                </Box>
              </>
            )}
            {isDestinationSelected('holded') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_EXPORT_DESTINATION_HOLDED')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.holded && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_HOLDED_API_KEY')}</Typography>
                    {saved.holded && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={holdedApikey}
                      style={styles.inputField}
                      onChange={(e) => setHoldedApikey(e.target.value)}
                      onBlur={(e) => saveNewDetailsHolded(e.target.value)}
                      disabled={loading || user?.userRole !== userRoles.admin}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onCheckConnectionHolded}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                  </Button>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onSyncHolded}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_QUICKBOOKS_SYNC')}
                  </Button>
                </Box>
              </>
            )}
            {selectedExportDestinations.includes('gstock') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_EXPORT_DESTINATION_GSTOCK')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.gstock && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_GSTOCK_CLIENT_ID')}</Typography>
                    {saved.gstockClientID && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={gstockClientID}
                      style={styles.inputField}
                      onChange={(e) => setGstockClientID(e.target.value)}
                      onBlur={(e) => saveNewDetailsGstock(e.target.value, gstockClientSecret, 0)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_GSTOCK_CLIENT_SECRET')}</Typography>
                    {saved.gstockClientSecret && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={gstockClientSecret}
                      style={styles.inputField}
                      onChange={(e) => setGstockClientSecret(e.target.value)}
                      onBlur={(e) => saveNewDetailsGstock(gstockClientID, e.target.value, 1)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onCheckConnectionGstock}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                  </Button>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onSyncGstock}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_QUICKBOOKS_SYNC')}
                  </Button>
                </Box>
              </>
            )}
            {selectedExportDestinations.includes('sftp') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_EXPORT_DESTINATION_SFTP')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.sftp && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_SFTP_HOST_NAME')}</Typography>
                    {saved.hostSFTP && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      name="host"
                      type="text"
                      value={sftpData?.host}
                      style={styles.inputField}
                      onChange={(e) => onChangeSFTPDetails(e)}
                      onBlur={(e) => saveSFTPDetails(e)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_SFTP_PORT_NUMBER')}</Typography>
                    {saved.portSFTP && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      name="port"
                      type="text"
                      value={sftpData?.port}
                      style={styles.inputField}
                      onChange={(e) => onChangeSFTPDetails(e)}
                      onBlur={(e) => saveSFTPDetails(e)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_SFTP_USERNAME')}</Typography>
                    {saved.usernameSFTP && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      name="username"
                      type="text"
                      value={sftpData?.username}
                      style={styles.inputField}
                      onChange={(e) => onChangeSFTPDetails(e)}
                      onBlur={(e) => saveSFTPDetails(e)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_SFTP_PASSWORD')}</Typography>
                    {saved.passwordSFTP && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      name="password"
                      type={showSftpPassword ? "text" : "password"}
                      value={sftpData?.password}
                      style={styles.inputField}
                      onChange={(e) => onChangeSFTPDetails(e)}
                      onBlur={(e) => saveSFTPDetails(e)}
                    />
                    {showSftpPassword
                      ? <IconButton sx={styles.copyBtn} onClick={() => setShowSftpPassword(false)}>
                        <HidePasswordIcon sx={styles.copyIcon} />
                      </IconButton>
                      : <IconButton sx={styles.copyBtn} onClick={() => setShowSftpPassword(true)}>
                        <ShowPasswordIcon sx={styles.copyIcon} />
                      </IconButton>
                    }
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="8px" alignItems="center">
                    <Typography sx={styles.label}>{t('INTEGRATIONS_SFTP_FOLDER_PATH')}</Typography>
                    {saved.folderPathSFTP && <Box sx={styles.savedChip}>{ready && t('INTEGRATIONS_SAVED')}</Box>}
                  </Box>
                  <Box sx={styles.detailRow}>
                    <input
                      name="folderPath"
                      type="text"
                      value={sftpData?.folderPath}
                      style={styles.inputField}
                      onChange={(e) => onChangeSFTPDetails(e)}
                      onBlur={(e) => saveSFTPDetails(e)}
                    />
                  </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Button
                    variant="outlined"
                    sx={styles.checkConnectionBtn}
                    onClick={onCheckConnectionSFTP}
                    disabled={loading}
                  >
                    {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                  </Button>
                </Box>
              </>
            )}
            {selectedExportDestinations.includes('api') && (
              <>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.deviderLabel}>
                    {t('WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_API')} {t('WORKFLOWS_COMMON_CONFIGURATION')}
                  </Typography>
                </Box>
                {appError.api && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.labelTextError}>{t('INTEGRATIONS_ERROR_RETRIEVE_DETAILS')}</Typography>
                  </Box>
                )}
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_REQUEST_METHOD')}</Typography>
                  <FormControl fullWidth sx={styles.formControl}>
                    <Select
                      name="method"
                      value={apiConfig.method}
                      onChange={(e) => handleApiConfigChange(e as any)}
                      input={<OutlinedInput />}
                      sx={styles.selectStyle}
                      disabled={loading}
                    >
                      <MenuItem sx={styles.selectItem} value="POST">POST</MenuItem>
                      <MenuItem sx={styles.selectItem} value="PUT">PUT</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_REQUEST_ENDPOINT')}</Typography>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      name="url"
                      value={apiConfig.url}
                      style={{
                        ...styles.inputField,
                        border: apiConfig.url && !isValidUrl(apiConfig.url) ? '1px solid #d32f2f' : styles.inputField.border
                      }}
                      onChange={handleApiConfigChange}
                      disabled={loading}
                      placeholder="https://example.com/api/documents"
                    />
                  </Box>
                  {apiConfig.url && !isValidUrl(apiConfig.url) && (
                    <Typography sx={{ color: '#d32f2f', fontSize: 12, marginTop: '4px' }}>
                      {t('CUSTOMER_INTEGRATIONS_INVALID_URL')}
                    </Typography>
                  )}
                </Box>
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_AUTH_TYPE')}</Typography>
                  <FormControl fullWidth sx={styles.formControl}>
                    <Select
                      name="authorizationType"
                      value={apiConfig.authorizationType}
                      onChange={(e) => handleApiConfigChange(e as any)}
                      input={<OutlinedInput />}
                      sx={styles.selectStyle}
                      disabled={loading}
                    >
                      <MenuItem sx={styles.selectItem} value="basic-auth">Basic Auth</MenuItem>
                      <MenuItem sx={styles.selectItem} value="bearer-token">Bearer Token</MenuItem>
                      <MenuItem sx={styles.selectItem} value="api-key">API Key</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {(!apiConfig.authorizationType || apiConfig.authorizationType === 'basic-auth') && (
                  <>
                    <Box sx={styles.fieldContainer}>
                      <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_USERNAME')}</Typography>
                      <Box sx={styles.detailRow}>
                        <input
                          type="text"
                          name="username"
                          value={apiConfig.username}
                          style={styles.inputField}
                          onChange={handleApiConfigChange}
                          disabled={loading}
                        />
                      </Box>
                    </Box>
                    <Box sx={styles.fieldContainer}>
                      <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_PASSWORD')}</Typography>
                      <Box sx={styles.detailRow}>
                        <input
                          type={showApiPassword ? 'text' : 'password'}
                          name="password"
                          value={apiConfig.password}
                          style={styles.inputField}
                          onChange={handleApiConfigChange}
                          disabled={loading}
                        />
                        <IconButton sx={styles.copyBtn} onClick={() => setShowApiPassword(!showApiPassword)}>
                          {showApiPassword ? <HidePasswordIcon sx={styles.copyIcon} /> : <ShowPasswordIcon sx={styles.copyIcon} />}
                        </IconButton>
                      </Box>
                    </Box>
                  </>
                )}

                {apiConfig.authorizationType === 'bearer-token' && (
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_BEARER_TOKEN')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type={showApiToken ? 'text' : 'password'}
                        name="bearerToken"
                        value={apiConfig.bearerToken}
                        style={styles.inputField}
                        onChange={handleApiConfigChange}
                        disabled={loading}
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => setShowApiToken(!showApiToken)}>
                        {showApiToken ? <HidePasswordIcon sx={styles.copyIcon} /> : <ShowPasswordIcon sx={styles.copyIcon} />}
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {apiConfig.authorizationType === 'api-key' && (
                  <>
                    <Box sx={styles.fieldContainer}>
                      <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_API_KEY_NAME')}</Typography>
                      <Box sx={styles.detailRow}>
                        <input
                          type="text"
                          name="key"
                          value={apiConfig.key}
                          style={styles.inputField}
                          onChange={handleApiConfigChange}
                          disabled={loading}
                          placeholder="X-API-Key"
                        />
                      </Box>
                    </Box>
                    <Box sx={styles.fieldContainer}>
                      <Typography sx={styles.label}>{t('CUSTOMER_INTEGRATIONS_API_KEY_VALUE')}</Typography>
                      <Box sx={styles.detailRow}>
                        <input
                          type={showApiValue ? 'text' : 'password'}
                          name="value"
                          value={apiConfig.value}
                          style={styles.inputField}
                          onChange={handleApiConfigChange}
                          disabled={loading}
                        />
                        <IconButton sx={styles.copyBtn} onClick={() => setShowApiValue(!showApiValue)}>
                          {showApiValue ? <HidePasswordIcon sx={styles.copyIcon} /> : <ShowPasswordIcon sx={styles.copyIcon} />}
                        </IconButton>
                      </Box>
                    </Box>
                  </>
                )}

                <Box sx={styles.fieldContainer}>
                  <Box display="flex" gap="16px">
                    <Tooltip
                      title={!isApiFormValid() ? t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS') : ''}
                      placement="top"
                      arrow
                      disableHoverListener={isApiFormValid()}
                      disableFocusListener={isApiFormValid()}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          sx={styles.checkConnectionBtn}
                          onClick={onCheckApiConnection}
                          disabled={loading || !isApiFormValid()}
                        >
                          {loading && <CircularProgress size={16} sx={{ marginRight: '8px' }} />}
                          {ready && t('INTEGRATIONS_CHECK_CONNECTION')}
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip
                      title={!isApiFormValid() || !isApiFieldChanged ? t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS') : ''}
                      placement="top"
                      arrow
                      disableHoverListener={isApiFormValid() && isApiFieldChanged}
                      disableFocusListener={isApiFormValid() && isApiFieldChanged}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          sx={styles.checkConnectionBtn}
                          onClick={saveApiDetails}
                          disabled={loading || !isApiFormValid() || !isApiFieldChanged}
                          startIcon={<SaveIcon />}
                        >
                          {ready && t('CUSTOMER_INTEGRATIONS_SAVE')}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Grid>
        <Box sx={styles.modalFooter}>
          <Box display="flex" justifyContent="flex-end" gap="16px">
            <CancelBtn
              title={t('DOC_TYPES_TABLE_ADD_NEW_CANCEL')}
              onClick={onClose}
            />
            <SubmitBtn
              title={t('DOC_TYPES_FIELDS_MANAGE_SAVE')}
              disabled={isSaving || selectedExportFormat === '' || selectedExportDestinations.length === 0}
              onClick={handleDocumentExportSave}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default DocumentExportComponent;
